import React, { useState, useEffect } from "react";
import moment from "moment";
import helperFunctions from "../../../utils/functions";
import Bridge from "../../../utils/Bridge";
import { logEvent } from "../../../utils/analytics";
import { Spin, Tabs } from "antd";

const { TabPane } = Tabs;

const Alsfrsr = (props) => {
  // State hooks
  const [records, setRecords] = useState([]);
  const [inclinicRecords, setInclinicRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("digital");

  // Fetch data on component mount
  useEffect(() => {
    // Fetches both digital and in-clinic ALSFRS data
    const get_eals_frs_list = () => {
      setLoading(true);
      let params = "?eals_id=" + localStorage.getItem("ap_eals_id");
      
      // Fetch both digital and in-clinic data in parallel
      Promise.all([
        Bridge.personal_data_viewer.le_study.get_le_study_eals_frs_score(params),
        Bridge.personal_data_viewer.le_study.get_le_study_inclinic_alsfrs_score(params)
      ])
        .then(([digitalRes, inclinicRes]) => {
          // Sort digital records by date descending (newest first)
          const digitalData = digitalRes.status == "1" ? digitalRes.data : [];
          const sortedDigitalData = digitalData.sort((a, b) => {
            const dateA = a.created_at ? new Date(a.created_at) : new Date(0);
            const dateB = b.created_at ? new Date(b.created_at) : new Date(0);
            return dateB - dateA; // Descending order (newest first)
          });
          setRecords(sortedDigitalData);
          setInclinicRecords(inclinicRes.status == "1" ? inclinicRes.data : []);
          setLoading(false);
        })
        .catch((err) => {
          setLoading(false);
        });
    };
    
    get_eals_frs_list();
  }, []);
  
  // Handles tab change between Digital and In-clinic
  const handleTabChange = (key) => {
    const tabLabels = { digital: "Digital", inclinic: "In-Clinic" };
    logEvent("tab_selected", { tab: tabLabels[key] || key, section: "alsfrsr" });
    setActiveTab(key);
  };
  
  const formData = props.language;
  
  // Helper function to get value or 0 for in-clinic data
  const getValue = (value, isInClinic) => {
    if (isInClinic) {
      return value !== null && value !== undefined && value !== "" ? value : 0;
    }
    return value !== null && value !== undefined && value !== "" ? value : "---";
  };
    
  // Render table function to avoid code duplication
  const renderTable = (records, isInClinic = false) => {
    if (records.length === 0) {
      return (
        <div className="col-lg-12">
          <div
            style={{
              fontSize: 20,
              textAlign: "center",
              padding: 20,
            }}
          >
            {props.language.page.notAvailable}
          </div>
        </div>
      );
    }
      
    return (
      <div style={{ overflowX: "auto", width: "100%" }}>
        <table style={{ minWidth: "1200px", width: "100%" }}>
          <tr>
            <th style={{ minWidth: "100px", width: "100px" }}>{formData.page.personalDataViewer.alsfrsr.date}</th>
            <th style={{ minWidth: "80px", width: "80px" }}>{formData.page.personalDataViewer.alsfrsr.speech}</th>
            <th style={{ minWidth: "100px", width: "100px" }}>{formData.page.personalDataViewer.alsfrsr.salvation}</th>
            <th style={{ minWidth: "120px", width: "120px" }}>{formData.page.personalDataViewer.alsfrsr.swallowing}</th>
            <th style={{ minWidth: "120px", width: "120px" }}>{formData.page.personalDataViewer.alsfrsr.handwriting}</th>
            <th style={{ minWidth: "120px", width: "120px" }}>{formData.page.personalDataViewer.alsfrsr.cuttingFood}</th>
            <th style={{ minWidth: "180px", width: "180px" }}>{formData.page.personalDataViewer.alsfrsr.dressingAndHygiene}</th>
            <th style={{ minWidth: "150px", width: "150px" }}>{formData.page.personalDataViewer.alsfrsr.turningInBed}</th>
            <th style={{ minWidth: "100px", width: "100px" }}>{formData.page.personalDataViewer.alsfrsr.walking}</th>
            <th style={{ minWidth: "140px", width: "140px" }}>{formData.page.personalDataViewer.alsfrsr.climbingStairs}</th>
            <th style={{ minWidth: "100px", width: "100px" }}>{formData.page.personalDataViewer.alsfrsr.dyspnea}</th>
            <th style={{ minWidth: "110px", width: "110px" }}>{formData.page.personalDataViewer.alsfrsr.orthopnea}</th>
            <th style={{ minWidth: "200px", width: "200px" }}>{formData.page.personalDataViewer.alsfrsr.respiratoryInsufficiency}</th>
            <th style={{ minWidth: "80px", width: "80px" }}>{formData.page.personalDataViewer.alsfrsr.total}</th>
          </tr>
        {records &&
          records.map((item, index) => {
            const dateVal = isInClinic ? item.session_date : item.created_at;
            let speechValue = getValue(item.speech, isInClinic);
            let salvationValue = getValue(item.salvation, isInClinic);
            let swallowingValue = getValue(item.swallowing, isInClinic);
            let handwritingValue = getValue(item.handwriting, isInClinic);
            let cutting_food_value = "";
            if (item.cutting_food_a !== null && item.cutting_food_a !== undefined && item.cutting_food_a !== "") {
              cutting_food_value = getValue(item.cutting_food_a, isInClinic);
            } else if (item.cutting_food_b !== null && item.cutting_food_b !== undefined && item.cutting_food_b !== "") {
              cutting_food_value = getValue(item.cutting_food_b, isInClinic);
            } else {
              cutting_food_value = isInClinic ? 0 : "---";
            }
            let dressingValue = getValue(item.dressing_and_hygiene, isInClinic);
            let turningValue = getValue(item.turning_in_bed, isInClinic);
            let walkingValue = getValue(item.walking, isInClinic);
            let climbingValue = getValue(item.climbing_stairs, isInClinic);
            let dyspneaValue = getValue(item.dyspnea, isInClinic);
            let orthopneaValue = getValue(item.orthopnea, isInClinic);
            let respiratoryValue = getValue(item.respiratory_insufficiency, isInClinic);
            let totalValue = getValue(item.ALS_total, isInClinic);

            let speechColor = helperFunctions.getColor(speechValue);
            let salavationColor = helperFunctions.getColor(salvationValue);
            let swallowingColor = helperFunctions.getColor(swallowingValue);
            let handwritingColor = helperFunctions.getColor(handwritingValue);
            let cuttingFoodColor = helperFunctions.getColor(cutting_food_value);
            let dressingColor = helperFunctions.getColor(dressingValue);
            let turingColor = helperFunctions.getColor(turningValue);
            let walkingColor = helperFunctions.getColor(walkingValue);
            let climbingColor = helperFunctions.getColor(climbingValue);
            let dyspneaColor = helperFunctions.getColor(dyspneaValue);
            let orthopneaColor = helperFunctions.getColor(orthopneaValue);
            let respiratoryColor = helperFunctions.getColor(respiratoryValue);

            return (
              <tr key={index}>
                <td style={{ minWidth: "100px", width: "100px" }}>
                  {dateVal
                    ? moment(dateVal).format("MM/DD/YYYY")
                    : "---"}
                </td>
                <td class={speechColor} style={{ minWidth: "80px", width: "80px" }}>{speechValue}</td>
                <td class={salavationColor} style={{ minWidth: "100px", width: "100px" }}>{salvationValue}</td>
                <td class={swallowingColor} style={{ minWidth: "120px", width: "120px" }}>{swallowingValue}</td>
                <td class={handwritingColor} style={{ minWidth: "120px", width: "120px" }}>{handwritingValue}</td>
                <td class={cuttingFoodColor} style={{ minWidth: "120px", width: "120px" }}>{cutting_food_value}</td>
                <td class={dressingColor} style={{ minWidth: "180px", width: "180px" }}>{dressingValue}</td>
                <td class={turingColor} style={{ minWidth: "150px", width: "150px" }}>{turningValue}</td>
                <td class={walkingColor} style={{ minWidth: "100px", width: "100px" }}>{walkingValue}</td>
                <td class={climbingColor} style={{ minWidth: "140px", width: "140px" }}>{climbingValue}</td>
                <td className={dyspneaColor} style={{ minWidth: "100px", width: "100px" }}>{dyspneaValue}</td>
                <td className={orthopneaColor} style={{ minWidth: "110px", width: "110px" }}>{orthopneaValue}</td>
                <td className={respiratoryColor} style={{ minWidth: "200px", width: "200px" }}>{respiratoryValue}</td>
                <td style={{ minWidth: "80px", width: "80px" }}>{totalValue}</td>
              </tr>
            );
          })}
        </table>
      </div>
    );
  };
  
  return (
    <div className="als_frs_sessions">
      <div className="container">
        <div className="row">
          <div className="col-lg-12 px-0">
            <Spin spinning={loading}>
              <Tabs
                activeKey={activeTab}
                onChange={handleTabChange}
                type="card"
              >
                <TabPane tab={formData.page.personalDataViewer.alsfrsr.digitalTab} key="digital">
                  {renderTable(records, false)}
                </TabPane>
                <TabPane tab={formData.page.personalDataViewer.alsfrsr.inclinicTab} key="inclinic">
                  {renderTable(inclinicRecords, true)}
                </TabPane>
              </Tabs>
            </Spin>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Alsfrsr;
